import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Compass, Home as HomeIcon, MapPin, ArrowRight, ChevronLeft, ChevronRight,
  RefreshCw, Loader2, Sparkles, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const HOME_LS_KEY = "driver_home_coords_v1";

function getToken() { return localStorage.getItem("driver_token"); }

function readHome() {
  try {
    const raw = localStorage.getItem(HOME_LS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.lat === "number" && typeof v?.lng === "number") return v;
    return null;
  } catch { return null; }
}

function writeHome(coords) {
  try { localStorage.setItem(HOME_LS_KEY, JSON.stringify(coords)); } catch { /* ignore */ }
}

function clearHome() {
  try { localStorage.removeItem(HOME_LS_KEY); } catch { /* ignore */ }
}

function scoreColor(score) {
  if (score >= 0.8) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (score >= 0.5) return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function scoreLabel(score) {
  if (score >= 0.8) return "Excellent fit";
  if (score >= 0.5) return "Decent fit";
  return "Stretch";
}

/**
 * Return-loads carousel. Always rendered on the driver Home tab.
 *
 * - When driver.status is "returning_empty" (or "available") and home coords are
 *   set, polls /api/driver/return-loads every 60s and renders horizontal
 *   scrollable cards.
 * - When status not eligible, shows a friendly explainer card.
 * - When home coords missing, shows "Set Home Location" CTA that captures
 *   browser geolocation and stores it in localStorage.
 *
 * Accepting a job uses the existing /api/driver/jobs/{id}/accept endpoint via
 * the parent's `onAccept` callback so we don't duplicate state-handling logic.
 */
export default function ReturnLoadsCarousel({ driverStatus, onAccept, acceptLoading }) {
  const [home, setHome] = useState(readHome());
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState(null);
  const scrollerRef = useRef(null);

  const eligible = driverStatus === "returning_empty" || driverStatus === "available";

  const fetchMatches = useCallback(async () => {
    if (!eligible || !home) return;
    setLoading(true);
    try {
      const url = `${API}/api/driver/return-loads?home_lat=${home.lat}&home_lng=${home.lng}&max_results=10`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) {
        setMatches([]);
        setReason(`server_error_${res.status}`);
        return;
      }
      const data = res.data;
      if (data?.available === false) {
        setMatches([]);
        setReason(data.reason || "status_not_eligible");
      } else {
        setMatches(Array.isArray(data?.matches) ? data.matches : []);
        setReason(null);
      }
    } catch (e) {
      setMatches([]);
      setReason("network_error");
    } finally {
      setLoading(false);
    }
  }, [eligible, home]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  useEffect(() => {
    if (!eligible || !home) return;
    const t = setInterval(fetchMatches, 60000);
    return () => clearInterval(t);
  }, [fetchMatches, eligible, home]);

  const captureHome = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    toast.info("Capturing your home location…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        writeHome(coords);
        setHome(coords);
        toast.success("Home location saved", {
          description: "We'll surface back-haul jobs on your return route.",
        });
      },
      (err) => {
        toast.error("Couldn't get your location", {
          description: err.message || "Please allow location access.",
        });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const resetHome = () => {
    clearHome();
    setHome(null);
    setMatches([]);
    toast.info("Home location cleared");
  };

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
  };

  // ============ Render gates ============
  // 1. Not eligible → explanatory card (always visible per user choice).
  if (!eligible) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="return-loads-disabled">
        <div className="flex items-start gap-3">
          <Compass className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-700">Return-load suggestions</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Switch your status to <span className="font-semibold text-cyan-600">Returning Empty</span> after a job
              to see back-haul opportunities on your route home.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 2. Eligible but home not set → CTA.
  if (!home) {
    return (
      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4" data-testid="return-loads-set-home">
        <div className="flex items-start gap-3">
          <HomeIcon className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-cyan-900">Set your home location</p>
            <p className="text-xs text-cyan-700 mt-0.5">
              Tap below from where you'd like return loads matched — we'll suggest jobs on your way back.
            </p>
            <Button
              onClick={captureHome}
              className="mt-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs px-4 py-1.5 h-auto rounded-lg"
              data-testid="return-loads-capture-home-btn"
            >
              <MapPin className="w-3.5 h-3.5 mr-1" /> Use my current location
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Eligible + home set → carousel.
  return (
    <div data-testid="return-loads-carousel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-600" />
          <p className="text-sm font-semibold text-slate-700">
            Return loads
            {matches.length > 0 && <span className="text-slate-400 font-normal"> ({matches.length})</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchMatches}
            className="p-1.5 text-slate-400 hover:text-slate-700"
            title="Refresh return loads"
            data-testid="return-loads-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={resetHome}
            className="text-[11px] text-slate-400 hover:text-slate-700 underline"
            data-testid="return-loads-reset-home"
          >
            Reset home
          </button>
        </div>
      </div>

      {loading && matches.length === 0 && (
        <div className="text-center py-6 text-slate-400" data-testid="return-loads-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}

      {!loading && matches.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center" data-testid="return-loads-empty">
          <Info className="w-5 h-5 mx-auto text-slate-300 mb-1" />
          <p className="text-xs text-slate-500">
            No back-haul jobs match your route right now.
            {reason === "status_not_eligible" && " Status must be Returning Empty or Available."}
          </p>
        </div>
      )}

      {matches.length > 0 && (
        <div className="relative">
          {/* Scroll buttons (desktop) */}
          <button
            onClick={() => scroll(-1)}
            className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center text-slate-500 hover:text-slate-800 z-10"
            data-testid="return-loads-scroll-left"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            onClick={() => scroll(1)}
            className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center text-slate-500 hover:text-slate-800 z-10"
            data-testid="return-loads-scroll-right"
          ><ChevronRight className="w-4 h-4" /></button>

          <div
            ref={scrollerRef}
            className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin"
            data-testid="return-loads-scroller"
            style={{ scrollbarWidth: "thin" }}
          >
            {matches.map((m) => {
              const payout = m.total_cost ? m.total_cost * 0.75 : null;
              return (
                <div
                  key={m.booking_id}
                  className="snap-start flex-shrink-0 w-[260px] bg-white border border-slate-200 rounded-xl p-3 shadow-sm"
                  data-testid={`return-load-card-${m.booking_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${scoreColor(m.score)}`}
                      data-testid={`return-load-score-${m.booking_id}`}
                    >
                      {scoreLabel(m.score)}
                    </span>
                    {payout != null && (
                      <span className="text-sm font-bold text-green-600" data-testid={`return-load-payout-${m.booking_id}`}>
                        &pound;{payout.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-slate-700 font-mono mb-2">
                    <span className="truncate">{m.pickup_postcode || "?"}</span>
                    <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{m.delivery_postcode || "?"}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 text-[11px] text-slate-500 mb-3">
                    <div>
                      <span className="text-slate-400 block">Detour</span>
                      <span className="font-semibold text-slate-700">+{m.detour_miles?.toFixed(1)} mi</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Total via</span>
                      <span className="font-semibold text-slate-700">{m.total_route_miles?.toFixed(0)} mi</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => onAccept?.(m.booking_id)}
                    disabled={acceptLoading}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-xs py-2 h-auto rounded-lg"
                    data-testid={`return-load-accept-${m.booking_id}`}
                  >
                    {acceptLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Accept return load
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
